import React from "react";
import { Box, Typography } from "@mui/material";
import { Inbox } from "@mui/icons-material";
import { Button, type ButtonVariant } from "./Button";

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: ButtonVariant;
  };
}

/**
 * Composant EmptyState pour afficher un état vide
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="Aucune commande"
 *   description="Créez votre première commande pour commencer"
 *   action={{
 *     label: "Nouvelle commande",
 *     onClick: handleCreate,
 *     variant: "primary"
 *   }}
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          mb: 3,
          color: "text.secondary",
          "& svg": {
            fontSize: 64,
          },
        }}
      >
        {icon || <Inbox />}
      </Box>

      <Typography
        variant="h5"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: "text.primary",
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          sx={{
            mb: 4,
            maxWidth: "400px",
            color: "text.secondary",
          }}
        >
          {description}
        </Typography>
      )}

      {action && (
        <Button
          variant={action.variant ?? "primary"}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
};
